// Renders the "Category Leaders" collapsible panel that shows the top 5 free
// and top 5 paid apps in the same App Store category for a selected country.
//
// The panel is lazy-loaded on first open of the <details> element to avoid
// triggering network requests before the user has asked for this data.

import { t, getLocale, countryName as i18nCountryName } from '../i18n/index.js';
import { fetchCategoryLeaders, formatLeaderSections, isFeedBlocked } from '../domain/categoryLeaders.js';
import { isChartEnrichmentEnabled } from '../config.js';
import { flagEmoji } from '../domain/flagEmoji.js';
import { STOREFRONTS } from '../data/storefronts.js';
import { genreName } from '../data/genres.js';

function esc(value) {
  return String(value ?? '').replace(/[&<>"']/g, (ch) => {
    switch (ch) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
    }
    return ch;
  });
}

/**
 * Build the country selector <select> element for the panel.
 * @param {string} defaultCountry - Two-letter code for the initially selected option.
 * @returns {string} HTML string.
 */
function renderCountrySelect(defaultCountry) {
  const options = STOREFRONTS.map(({ code, name }) => {
    const label = i18nCountryName(code, name);
    const selected = code === defaultCountry ? ' selected' : '';
    return `<option value="${esc(code)}"${selected}>${flagEmoji(code)} ${esc(label)}</option>`;
  }).join('');
  return `<select class="leaders__country-select" aria-label="${esc(t('leaders.selectCountry'))}">${options}</select>`;
}

/**
 * Render a list of CategoryLeader entries as an HTML table section.
 * @param {import('../domain/categoryLeaders.js').CategoryLeader[]} entries
 * @param {string} headingKey - i18n key for the section heading.
 * @returns {string} HTML string.
 */
function renderLeaderList(entries, headingKey) {
  if (entries.length === 0) {
    return `<p class="leaders__empty muted">${esc(t('leaders.noData'))}</p>`;
  }
  const rows = entries.map(
    (e) =>
      `<tr>
        <td class="leaders__rank">${esc(String(e.rank))}</td>
        <td class="leaders__name">${esc(e.name ?? `ID ${e.trackId}`)}</td>
      </tr>`,
  ).join('');
  return `
    <h4 class="leaders__section-heading">${esc(t(headingKey))}</h4>
    <table class="leaders__table">
      <tbody>${rows}</tbody>
    </table>`;
}

/**
 * Mount the Category Leaders panel into a container element.
 * Lazy-loads data on first open of the <details> toggle.
 *
 * @param {object} opts
 * @param {string} opts.genreId - Apple genre ID for the app being viewed.
 * @param {string} opts.defaultCountry - Two-letter country code to start with.
 * @param {HTMLElement} opts.container - DOM element to render into.
 */
export function renderCategoryLeaders({ genreId, defaultCountry, container }) {
  if (!container || !genreId) return;

  const label = genreName(genreId, getLocale());

  container.innerHTML = `
    <details class="leaders-panel" id="leaders-panel">
      <summary class="leaders-panel__summary">
        ${esc(t('leaders.panelTitle', { genre: label }))}
      </summary>
      <div class="leaders-panel__body">
        <div class="leaders__controls">
          ${renderCountrySelect(defaultCountry)}
        </div>
        <div class="leaders__content" aria-live="polite">
          <p class="leaders__loading muted">${esc(t('leaders.loading'))}</p>
        </div>
      </div>
    </details>`;

  const details = container.querySelector('#leaders-panel');
  const content = container.querySelector('.leaders__content');
  const select = container.querySelector('.leaders__country-select');

  let loaded = false;

  async function load(country) {
    content.innerHTML = `<p class="leaders__loading muted">${esc(t('leaders.loading'))}</p>`;
    try {
      const result = await fetchCategoryLeaders(country, genreId);
      if (isFeedBlocked(result) && !isChartEnrichmentEnabled()) {
        content.innerHTML = `<p class="leaders__cors-note muted">${esc(t('leaders.corsNote'))}</p>`;
        return;
      }
      const { free, paid } = formatLeaderSections(result);
      content.innerHTML = `
        <div class="leaders__columns">
          <div class="leaders__col">
            ${renderLeaderList(free, 'leaders.topFree')}
          </div>
          <div class="leaders__col">
            ${renderLeaderList(paid, 'leaders.topPaid')}
          </div>
        </div>`;
    } catch {
      content.innerHTML = `<p class="leaders__error muted">${esc(t('leaders.loadError'))}</p>`;
    }
  }

  details.addEventListener('toggle', () => {
    if (details.open && !loaded) {
      loaded = true;
      load(select.value);
    }
  });

  select.addEventListener('change', () => {
    if (details.open) {
      loaded = true;
      load(select.value);
    }
  });
}
